/**
 * 我的日曆 API Service
 * 注意：axios 攔截器自動從 localStorage 讀取 txn_token 並添加到所有請求的 header
 * Service 層不需要手動傳遞 txn_token 參數
 */

import axiosInstance from '../api/axios';
import { EeMyCalendar, EeMyCalendarCreate, EeMyCalendarUpdate } from '../types/eeMyCalendar';

const BASE = '/api/ee_my_calendar';

// ownerUserId：助手代他人操作時明帶（stateless · 每 request 帶目標 owner）。null/undefined=自己。
const ownerParam = (ownerUserId?: number | null) =>
  (ownerUserId != null ? { owner_user_id: ownerUserId } : {});

// E6b-c：日曆 client 設定（只取 Google 公開金鑰·FullCalendar 節日用）。
// 取代原本讀平台管理專用的 /api/ee/settings（enforce 後一般使用者會 403）。
export const getMyCalendarConfig = async (): Promise<{ calendar_api_key: string }> => {
  const res = await axiosInstance.get<{ calendar_api_key: string }>(`${BASE}/config`);
  return res.data;
};

export const getEvents = async (params?: {
  start?: string;
  end?: string;
  skip?: number;
  limit?: number;
  expand?: boolean;   // true=後端展開循環行程的每一次（手機列表用）；桌機不帶，自行用 rrule 展開
  ownerUserId?: number | null;
}): Promise<EeMyCalendar[]> => {
  const { ownerUserId, ...rest } = params || {};
  const res = await axiosInstance.get<EeMyCalendar[]>(`${BASE}/`,
    { params: { ...rest, ...ownerParam(ownerUserId) } });
  return res.data;
};

export const getEvent = async (id: number, ownerUserId?: number | null): Promise<EeMyCalendar> => {
  const res = await axiosInstance.get<EeMyCalendar>(`${BASE}/${id}`, { params: ownerParam(ownerUserId) });
  return res.data;
};

// §F 忙碌查詢（H.2 成員選擇器用）：每位成員在 [start,end) 的忙碌時段
export interface FreeBusyBlock { start: string; end: string; status: 'busy' | 'tentative'; label?: string; }
export const getFreeBusy = async (userIds: number[], start: string, end: string): Promise<Record<number, FreeBusyBlock[]>> => {
  const res = await axiosInstance.get<Record<number, FreeBusyBlock[]>>(`${BASE}/free-busy`,
    { params: { user_ids: userIds.join(','), start, end } });
  return res.data;
};

// force：使用者在「事件繁忙提醒」確認「仍要儲存」後帶 true，後端跳過衝突擋
const forceParam = (force?: boolean) => (force ? { force: true } : {});

export const createEvent = async (data: EeMyCalendarCreate, ownerUserId?: number | null, force?: boolean): Promise<EeMyCalendar> => {
  const res = await axiosInstance.post<EeMyCalendar>(`${BASE}/`, data,
    { params: { ...ownerParam(ownerUserId), ...forceParam(force) } });
  return res.data;
};

export const updateEvent = async (id: number, data: EeMyCalendarUpdate, ownerUserId?: number | null, force?: boolean): Promise<EeMyCalendar> => {
  const res = await axiosInstance.put<EeMyCalendar>(`${BASE}/${id}`, data,
    { params: { ...ownerParam(ownerUserId), ...forceParam(force) } });
  return res.data;
};

// ── 時間衝突檢查（儲存前 pre-check）────────────────────────────────────────────
export interface ConflictItem {
  id: number;
  summary: string;
  dtstart: string;
  dtend?: string | null;
  is_all_day: boolean;
  location?: string | null;
}
export interface ConflictCheckResponse {
  has_conflict: boolean;
  conflicts: ConflictItem[];
}

export const checkConflict = async (
  payload: { dtstart: string; dtend?: string | null; is_all_day?: boolean; exclude_event_id?: number | null },
  ownerUserId?: number | null,
): Promise<ConflictCheckResponse> => {
  const res = await axiosInstance.post<ConflictCheckResponse>(
    `${BASE}/check-conflict`, payload, { params: ownerParam(ownerUserId) });
  return res.data;
};

export const deleteEvent = async (id: number, ownerUserId?: number | null): Promise<void> => {
  await axiosInstance.delete(`${BASE}/${id}`, { params: ownerParam(ownerUserId) });
};

// ── 行事曆助手（assistant delegation）──────────────────────────────────────────
export interface CalendarAssistant {
  id: number;
  assistant_user_id: number;
  assistant_username?: string | null;
  assistant_account?: string | null;
  granted_at: string;
}
export interface CalendarScope {
  owner_user_id: number;
  owner_username?: string | null;
  owner_account?: string | null;
  granted_at: string;
}

export const listAssistants = async (): Promise<CalendarAssistant[]> => {
  const res = await axiosInstance.get<CalendarAssistant[]>(`${BASE}/assistants`);
  return res.data;
};

export const addAssistant = async (assistantUserId: number): Promise<CalendarAssistant> => {
  const res = await axiosInstance.post<CalendarAssistant>(`${BASE}/assistants`, { assistant_user_id: assistantUserId });
  return res.data;
};

export const revokeAssistant = async (recordId: number): Promise<void> => {
  await axiosInstance.delete(`${BASE}/assistants/${recordId}`);
};

/** 我在幫哪些人管理行事曆（供頂部身分切換 dropdown）。 */
export const getMyScopes = async (): Promise<CalendarScope[]> => {
  const res = await axiosInstance.get<CalendarScope[]>(`${BASE}/assistants/my-scopes`);
  return res.data;
};

export const notifyEvent = async (id: number): Promise<{ sent: number; skipped: number }> => {
  const res = await axiosInstance.post<{ sent: number; skipped: number }>(`${BASE}/${id}/notify`);
  return res.data;
};

// ── 附件（整合 my_drive）────────────────────────────────────────────────────
export const uploadAttachment = async (eventId: number, file: File): Promise<number> => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axiosInstance.post<number>(
    `${BASE}/${eventId}/attachments/upload`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
};

export const pickAttachment = async (eventId: number, fileId: number): Promise<void> => {
  await axiosInstance.post(`${BASE}/${eventId}/attachments/pick`, { file_id: fileId });
};

export const removeAttachment = async (eventId: number, fileId: number): Promise<void> => {
  await axiosInstance.delete(`${BASE}/${eventId}/attachments/${fileId}`);
};
